import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { useProject } from '../../context/ProjectContext';


interface Project {
  id: number;
  name: string;
  description?: string;
  github_repo?: string;
  jira_project_key?: string;
  jira_cloud_id?: string;
  is_active: boolean;
  user_role?: string;
  is_default: boolean;
}

interface ProjectOverviewProps {
  project: Project;
  onRefresh: () => void;
}

const ProjectOverview: React.FC<ProjectOverviewProps> = ({ project, onRefresh }) => {
  const { setAsDefault } = useProject();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: project.name,
    description: project.description || ''
  });

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API_URL}/api/projects/${project.id}`, formData);
      await onRefresh();
      setIsEditing(false);
      alert('Project updated successfully!');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to update project');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async () => {
    try {
      await setAsDefault(project.id);
      alert('Default project updated!');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to set default project');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete project "${project.name}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}`);
      await onRefresh();
      alert('Project deleted successfully!');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {project.name}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            {project.is_default && (
              <span className="px-2 py-0.5 text-xs bg-green-500 text-white rounded">
                Default Project
              </span>
            )}
            {project.user_role && (
              <span className="px-2 py-0.5 text-xs bg-gray-500 text-white rounded capitalize">
                {project.user_role}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          {!project.is_default && (
            <button
              onClick={handleSetDefault}
              className="px-4 py-2 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-800 transition"
            >
              Set as Default
            </button>
          )}
          {project.user_role === 'admin' && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              Edit
            </button>
          )}
          {project.user_role === 'admin' && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={handleUpdate} className="space-y-4 bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Project Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows={3}
              placeholder="Brief description of this project..."
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 space-y-4">
          {project.description ? (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Description</h3>
              <p className="text-gray-900 dark:text-white">{project.description}</p>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 dark:text-gray-500">
              <p>No description provided.</p>
              <button
                onClick={() => setIsEditing(true)}
                className="mt-2 text-blue-600 dark:text-blue-400 hover:underline text-sm"
              >
                Add a description
              </button>
            </div>
          )}

          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm text-blue-800 dark:text-blue-300">
            💡 <strong>Tip:</strong> Configure GitHub and Jira integrations in the <strong>Integrations</strong> tab.
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectOverview;
